import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { assertUnreachable } from '../../../../../../react-commons/utils/utilFunctions';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { get } from '../../../resource-base/APIVersion1';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceMultiParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceMultiParentedResourceAdapterActions';
import {
    createIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchema,
    defaultIndirectlyVersionedAndNamedArtifactResource,
} from '../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../resource-base/baseSchemas';
import { ParamConstants } from '../../../resource-base/types';
import { copyOverExistingProperties } from '../../utilFunctions';

export enum ContentPropertyValidation {
    NAME = 'NAME',
    DESCRIPTION = 'DESCRIPTION',
    MODEL_ELEMENT = 'MODEL_ELEMENT',
    RELEVANT_ROLES = 'RELEVANT_ROLES',
}

export enum ContentParentRelation {
    ACTIVITIES_MODEL = 'ACTIVITIES_MODEL',
}

const ContentParentRelationSchema = z.enum(ContentParentRelation);

export const ContentResourceBaseSchema = createIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchema(
    ContentParentRelationSchema
).extend({
    resourceType: z.literal(ResourceType.CONTENT).default(ResourceType.CONTENT),
    hasContentSections: z.boolean().optional(),
});

const ActivitiesModelElementContentResourceSchema = ContentResourceBaseSchema.extend({
    parentRelation: z.literal(ContentParentRelation.ACTIVITIES_MODEL),
    modelElement: z.string(),
    relevantRoles: IdSchema.array(),
});

export const ContentResourceSchema = ActivitiesModelElementContentResourceSchema;

export const ContentMutationResourceSchema = getMutationSchema(ActivitiesModelElementContentResourceSchema);

export type ContentResource = z.infer<typeof ContentResourceSchema>;

export function getDefaultContentResource(
    parentRelation: ContentParentRelation,
    previousResource?: ContentResource
): ContentResource {
    const baseResource: z.infer<typeof ContentResourceBaseSchema> = {
        ...defaultIndirectlyVersionedAndNamedArtifactResource,
        resourceType: ResourceType.CONTENT,
        parentRelation: ContentParentRelation.ACTIVITIES_MODEL,
    };

    switch (parentRelation) {
        case ContentParentRelation.ACTIVITIES_MODEL: {
            const valueBase = {
                ...baseResource,
                modelElement: '',
                relevantRoles: [],
            };
            return {
                ...copyOverExistingProperties(valueBase, previousResource),
                parentRelation,
            };
        }
        default: {
            return assertUnreachable(parentRelation);
        }
    }
}

export const defaultContentResource: ContentResource = getDefaultContentResource(
    ContentParentRelation.ACTIVITIES_MODEL
);

export const defaultSubtypedContentResource: ContentResource = {
    ...defaultContentResource,
    modelElement: '',
    relevantRoles: [],
};

export function useContentActions() {
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceMultiParentedResourceAdapterActions('content', ResourceType.CONTENT, ContentResourceSchema),
        ...useIndirectlyVersionedResourceAdapter('content', ResourceType.CONTENT, ContentResourceSchema),
    };

    const getAllWithSectionResources = (
        parentId: string,
        parentRelation: string
    ): Promise<Array<ContentResource> | null> => {
        return get(
            `content/with-section?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}&${ParamConstants.PARENT_RELATION_REQUEST_PARAM_NAME}=${parentRelation}`
        ).then((result) => actions.processGetCollectionResponse<ContentResource>(result));
    };

    const useGetAllWithSectionResources = (parentRelation: string | undefined, parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, parentRelation, ResourceType.CONTENT, 'withSection'],
            queryFn: () => getAllWithSectionResources(parentId as string, parentRelation as string),
            enabled: getEnabled(parentId && parentRelation),
        });
    };

    return {
        ...actions,
        useGetAllWithSectionResources,
    };
}
